"use client";

import { useState, FormEvent } from "react";

type Status = "idle" | "loading" | "success" | "queued" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    setInfoMsg("");

    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      subject: (form.elements.namedItem("subject") as HTMLInputElement).value,
      message: (form.elements.namedItem("message") as HTMLTextAreaElement).value,
      newsletter: (form.elements.namedItem("newsletter") as HTMLInputElement).checked,
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = (await res.json().catch(() => ({}))) as {
        error?: string;
        queued?: boolean;
        message?: string;
      };
      if (!res.ok) {
        throw new Error(json.error ?? "Something went wrong");
      }
      if (json.queued) {
        setInfoMsg(json.message ?? "We already have a request from you in the queue today.");
        setStatus("queued");
      } else {
        setStatus("success");
      }
      form.reset();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
      setStatus("error");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-widest text-[#adaaab]" style={{ fontFamily: "var(--font-manrope), sans-serif" }}>Identity Name</label>
          <input
            name="name"
            type="text"
            required
            placeholder="John Doe"
            className="w-full bg-[#262627] border-none rounded-[1rem] p-4 focus:ring-2 focus:ring-[#cc97ff]/40 focus:bg-[#1f1f21] transition-all text-white placeholder:text-[#767576]/50 outline-none"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-widest text-[#adaaab]" style={{ fontFamily: "var(--font-manrope), sans-serif" }}>Digital Mail</label>
          <input
            name="email"
            type="email"
            required
            placeholder="contact@domain.com"
            className="w-full bg-[#262627] border-none rounded-[1rem] p-4 focus:ring-2 focus:ring-[#cc97ff]/40 focus:bg-[#1f1f21] transition-all text-white placeholder:text-[#767576]/50 outline-none"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-xs font-bold uppercase tracking-widest text-[#adaaab]" style={{ fontFamily: "var(--font-manrope), sans-serif" }}>Protocol / Subject</label>
        <input
          name="subject"
          type="text"
          placeholder="Brief subject"
          className="w-full bg-[#262627] border-none rounded-[1rem] p-4 focus:ring-2 focus:ring-[#cc97ff]/40 focus:bg-[#1f1f21] transition-all text-white placeholder:text-[#767576]/50 outline-none"
        />
      </div>
      <div className="space-y-2">
        <label className="text-xs font-bold uppercase tracking-widest text-[#adaaab]" style={{ fontFamily: "var(--font-manrope), sans-serif" }}>Mission Brief</label>
        <textarea
          name="message"
          required
          rows={5}
          placeholder="Describe the requirements for your project..."
          className="w-full bg-[#262627] border-none rounded-[1rem] p-4 focus:ring-2 focus:ring-[#cc97ff]/40 focus:bg-[#1f1f21] transition-all text-white placeholder:text-[#767576]/50 resize-none outline-none"
        />
      </div>
      <div className="flex items-center gap-3">
        <input
          name="newsletter"
          type="checkbox"
          id="newsletter"
          className="w-5 h-5 rounded border-none bg-[#262627] text-[#cc97ff] focus:ring-[#cc97ff]"
        />
        <label htmlFor="newsletter" className="text-sm text-[#adaaab]" style={{ fontFamily: "var(--font-manrope), sans-serif" }}>
          Receive updates from the network
        </label>
      </div>
      {status === "error" && (
        <p className="text-red-400 text-sm">{errorMsg}</p>
      )}
      {status === "success" && (
        <p className="text-[#cc97ff] text-sm font-bold">Message sent — we&apos;ll be in touch shortly.</p>
      )}
      {status === "queued" && (
        <p className="text-[#e197fc] text-sm font-bold">{infoMsg}</p>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full md:w-auto primary-gradient-cta text-[#000000] px-12 py-5 rounded-full font-extrabold uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-[0_10px_30px_-10px_rgba(204,151,255,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ fontFamily: "var(--font-plus-jakarta), sans-serif" }}
      >
        {status === "loading" ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
